import { m } from 'framer-motion';
import { useNavigate } from '@tanstack/react-router';
import { varHover, useAuthContext } from '@emfood/shared';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import { alpha } from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import CustomPopover, { usePopover } from 'src/components/custom-popover';

const OPTIONS = [
  {
    label: 'Home',
    linkTo: '/',
  },
  {
    label: 'Profile',
    linkTo: '/user',
  },
  {
    label: 'Change Password',
    linkTo: '/user/change-password',
  },
  {
    label: 'Documents',
    linkTo: 'https://equilibrium-markets.atlassian.net/wiki/x/AYDdhw',
    isUrl: true,
  },
  {
    label: 'Help',
    linkTo: 'https://equilibrium-markets.atlassian.net/servicedesk/customer/portals',
    isUrl: true,
  },
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const navigate = useNavigate();

  const { user, logout } = useAuthContext();

  const popover = usePopover();

  const handleLogout = async () => {
    try {
      await logout();
      popover.onClose();
      navigate({ to: '/auth/jwt/login', replace: true });
    } catch (error) {
      console.error(error);
    }
  };

  const handleClickItem = (path: string) => {
    popover.onClose();
    navigate({ to: path });
  };

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    const menuItems = document.querySelectorAll('[role="menuitem"]');
    const lastIndex = menuItems.length - 1;

    switch (event.key) {
      case 'ArrowDown':
      case 'Tab': {
        event.preventDefault();
        const nextIndex = index === lastIndex ? 0 : index + 1;
        (menuItems[nextIndex] as HTMLElement).focus();
        break;
      }
      case 'ArrowUp':
      case 'ShiftTab': {
        event.preventDefault();
        const prevIndex = index === 0 ? lastIndex : index - 1;
        (menuItems[prevIndex] as HTMLElement).focus();
        break;
      }
      case 'Home':
        event.preventDefault();
        (menuItems[0] as HTMLElement).focus();
        break;
      case 'End':
        event.preventDefault();
        (menuItems[lastIndex] as HTMLElement).focus();
        break;
      default:
        break;
    }
  };

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={popover.onOpen}
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={Boolean(popover.open)}
        aria-controls={popover.open ? 'account-menu' : undefined}
        sx={{
          width: 40,
          height: 40,
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
        }}
      >
        <Avatar
          src="/logo/em-leaf.png"
          alt="AgileChain logo"
          sx={{
            p: '5px',
            width: 36,
            height: 36,
            border: (theme) => `solid 2px ${theme.palette.background.default}`,
          }}
        >
          {user?.firstName?.charAt(0).toUpperCase()}
        </Avatar>
      </IconButton>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        sx={{ width: 200, p: 0 }}
        id="account-menu"
      >
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {user?.firstName}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }} role="menu">
          {OPTIONS.map((option, index) => (
            <MenuItem
              key={option.label}
              onClick={() => {
                if ('isUrl' in option && option.isUrl) {
                  window.open(option.linkTo, '_blank', 'noopener,noreferrer');
                } else {
                  handleClickItem(option.linkTo);
                }
              }}
              onKeyDown={(e) => handleKeyDown(e, index)}
              role="menuitem"
              tabIndex={0}
            >
              {option.label}
            </MenuItem>
          ))}

          <Divider sx={{ borderStyle: 'dashed' }} />

          <MenuItem
            onClick={handleLogout}
            sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'error.main' }}
            role="menuitem"
            tabIndex={0}
            onKeyDown={(e) => handleKeyDown(e, OPTIONS.length)}
          >
            Sign Out
          </MenuItem>
        </Stack>
      </CustomPopover>
    </>
  );
}
